/**
 * The ch01 feature manifest — impact measurement. bata-ui#132 step 5.
 *
 * The slug `strategic` was reserved for the long-range chapter and had no directory; the
 * product owner confirmed on 2026-09-26 that it holds "Impact Measurement" (step 0), with the
 * brand guide's long-range icon, `trending-up`, which no chapter used.
 *
 * Two screens, both a DRAFT on purpose and both over real data only:
 *
 * | Route | The question it answers |
 * |---|---|
 * | `impact` | What did each module's recent runs say they were worth — expected margin recovery per in-season cycle, net benefit per consolidation run |
 * | `test-vs-control` | Where the new cadence was run beside the incumbent, did it do better — gate D4's paired comparison |
 * | `monthly-pack` | The closed months of a market, frozen or not yet, each opening its pack (ADR 9016, `docs/adr/3002`; issue 226) |
 * | `monthly-pack/:month` | What management was handed for a closed month — the pack `programme.monthly_report` froze, as a «Documento» (ADR 9053; issue 226) |
 * | `override-outcomes` | How planners decided and what followed — the override rate per decision class (ADR 9015) |
 * | `override-outcomes/lines` | For one decision class (or every class), each decided line beside its projection — the class row's drill (issue 226) |
 * | `override-outcomes/by-run` | For one cycle run, the override rate per queue — replenishment, transfers, price steps (issue 226) |
 * | `matched-control` | The programme's matched-control estimate — doors matched to doors, difference in differences |
 * | `benefits` | Per benefit family, what the runs expected beside what the programme measured (ADR 9021) |
 *
 * The last three were panels of other pages (Impact, Test vs control, the Cockpit). One page, one
 * function (client rule 3, package A 2026-09-30): each is its own page now, drawn as a sub-entry
 * of Impact or Test vs control in the rail (`children`), and the parent URL stays the first page.
 *
 * Nothing here is a model of its own. Every figure is a stored field of a read `bata-platform`
 * wires (`impact.ts`, `control.ts` name them), and where the contract publishes nothing the
 * screen says so rather than drawing a number. No new backend was asked for (spec step 8); what
 * is missing is on the team board as follow-ups.
 */

import { defineFeature } from '../manifest';
import { STRATEGIC_GUIDES } from './guides';
import { healthByExport, healthExport, HEALTH_BY_EXPORT_ID, HEALTH_EXPORT_ID } from './health/exports';
import { lookupExport, LOOKUP_EXPORT_ID } from './health/lookup-exports';

import {
  controlExport, impactExport, monthlyPackExport, monthlyPackMonthsExport, CONTROL_EXPORT_ID, IMPACT_EXPORT_ID, PACK_EXPORT_ID, PACK_MONTHS_EXPORT_ID,
} from './exports';
import {
  BENEFITS_EXPORT_ID, MATCHED_EXPORT_ID, OVERRIDE_LINES_EXPORT_ID, OVERRIDES_BY_RUN_EXPORT_ID, OVERRIDES_EXPORT_ID, benefitsExport,
  matchedControlExport, overrideLinesExport, overrideOutcomesExport, overridesByRunExport,
} from './split-exports';

/** The scope every Health page reads (`health/reports.ts`, `readHealthScope`). */
const HEALTH_URL_PARAMS = ['by', 'country', 'department', 'measure', 'priceRunId', 'runId', 'week'];

export default defineFeature({
  slug: 'strategic',
  titleKey: 'strategic.title',
  navLabelKey: 'strategic.navLabel',
  icon: 'trending-up',  // brand guide p.14 — the long-range module's icon
  railGroup: 'setup',  // bata-ui#132 — analysis, beside foundations and post-season
  localeNamespace: 'strategic',
  // Every route's guide and guided tour (ADR 9044) — one entry per route below, or the census fails.
  guides: STRATEGIC_GUIDES,
  navigation: [
    {
      labelKey: 'strategic.nav.impact',
      to: '/strategic/impact',
      order: 20,
      children: [
        { labelKey: 'strategic.nav.impactByModule', to: '/strategic/impact', order: 1 },
        { labelKey: 'strategic.nav.benefits', to: '/strategic/benefits', order: 2 },
        { labelKey: 'strategic.nav.overrideOutcomes', to: '/strategic/override-outcomes', order: 3 },
        // Issue 226, giro 3 «Dopo» point 7: the fourth sub-entry — the menu has no fourth level,
        // so «Per esecuzione» stands beside Benefits rather than inside Override outcomes.
        { labelKey: 'strategic.nav.overrideOutcomesByRun', to: '/strategic/override-outcomes/by-run', order: 4 },
      ],
    },
    {
      labelKey: 'strategic.nav.testVsControl',
      to: '/strategic/test-vs-control',
      order: 21,
      children: [
        { labelKey: 'strategic.nav.d4Pairs', to: '/strategic/test-vs-control', order: 1 },
        { labelKey: 'strategic.nav.matchedControl', to: '/strategic/matched-control', order: 2 },
      ],
    },
    { labelKey: 'strategic.nav.monthlyPack', to: '/strategic/monthly-pack', order: 22 },
    // The Health app's four reports (bata-reqs ADR 9003, `docs/adr/3003`); the rail draws them under Health
    // (`../rail-apps.ts`, ADR 9079).
    { labelKey: 'strategic.nav.healthAvailability', to: '/strategic/health/availability', order: 30 },
    { labelKey: 'strategic.nav.healthCoverage', to: '/strategic/health/coverage', order: 31 },
    { labelKey: 'strategic.nav.healthSellThrough', to: '/strategic/health/sell-through', order: 32 },
    { labelKey: 'strategic.nav.healthMarkdown', to: '/strategic/health/markdown-exposure', order: 33 },
    // The spot check of our stock and sales against the client's own system (`docs/adr/3004`).
    { labelKey: 'strategic.nav.healthLookup', to: '/strategic/health/lookup', order: 34 },
  ],
  routes: [
    {
      path: 'impact',
      titleKey: 'strategic.impact.title',
      urlParams: [],
      component: () => import('./screens/ImpactScreen'),
      // Tiles for the newest run per module, a chart of the runs, and the runs as a table.
      surfaces: ['tile', 'chart', 'table'],
      tableViewReachable: true,
      exports: { id: IMPACT_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: impactExport },
      // A run's figure is a sum over that run's rows; the rows live on the chapter screens
      // that read them. The run is the grain here, and the drill leaves this chapter — for a price
      // run, to its calendar (issue 226: the run's recommendation left the markdown Output).
      drill: {
        to: '/inseason/markdown/calendar',
        grain: 'article',
        alsoTo: ['/exit/consolidation'],
      },
    },
    {
      path: 'test-vs-control',
      titleKey: 'strategic.control.title',
      urlParams: ['country', 'season'],
      component: () => import('./screens/TestVsControlScreen'),
      // The evaluable pairs as points against the diagonal (Package C6): only measured pairs are
      // drawn, so while none is evaluable there is no chart and the sentence says why.
      surfaces: ['tile', 'chart', 'table'],
      tableViewReachable: true,
      exports: { id: CONTROL_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: controlExport },
      drill: {
        terminal: true,
        reason:
          'The comparison is stored at option × cluster × shadow season, the primary key of plan.exit_shadow_comparison and the grain gate D4 is evaluated at. Below it lies the markdown variant that produced the test arm, a model artefact rather than a planning object.',
      },
    },
    {
      // Per benefit family, expected beside measured (ADR 9021) — the Cockpit's band until DEC-87.
      path: 'benefits',
      titleKey: 'overview.benefits.title',
      urlParams: ['exitRunId', 'figures', 'runId'],
      component: () => import('./screens/BenefitsScreen'),
      surfaces: ['tile'],
      exports: { id: BENEFITS_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: benefitsExport },
      drill: { to: '/strategic/impact', grain: 'day', alsoTo: ['/strategic/matched-control'] },
    },
    {
      path: 'override-outcomes',
      titleKey: 'strategic.overrides.title',
      // `overridden` and `projectionArchived` are written into the links of the cards that open the lines they count (ADR 9069).
      urlParams: ['asOf', 'class', 'country', 'overridden', 'projectionArchived'],
      component: () => import('./screens/OverrideOutcomesScreen'),
      surfaces: ['table'],
      exports: { id: OVERRIDES_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: overrideOutcomesExport },
      // Each class row opens its decided lines (issue 226, giro 3 «Dopo» point 7): the pairs table
      // that sat at the foot of this page is the class's drill level, on a page of its own.
      drill: { to: '/strategic/override-outcomes/lines', grain: 'transaction' },
    },
    {
      // «Righe» — a class's decided lines, each beside its projection. A drill, not in the rail: it is
      // reached from a class row and climbed from its breadcrumb. `?country=&asOf=&class=`, and the narrowing the
      // override cards count: `?overridden=` and `?projectionArchived=` (ADR 9069).
      path: 'override-outcomes/lines',
      titleKey: 'strategic.lines.title',
      urlParams: ['asOf', 'class', 'country', 'overridden', 'projectionArchived'],
      component: () => import('./screens/OverrideOutcomesLinesScreen'),
      surfaces: ['table'],
      exports: { id: OVERRIDE_LINES_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: overrideLinesExport },
      drill: {
        terminal: true,
        reason: 'A decided line beside the projection it was written with is the finest grain the override read publishes (bata-platform ADR 9015); the line itself is decided, and read, on its own queue.',
      },
    },
    {
      // Per run, how planners decided on each queue (issue 226, giro 3 «Dopo» points 6 and 7): the
      // panel that sat under Benefits, as template E. Run-scoped: `?runId=`, DEC-99 default.
      path: 'override-outcomes/by-run',
      titleKey: 'strategic.byRun.title',
      urlParams: ['runId'],
      component: () => import('./screens/OverrideOutcomesByRunScreen'),
      surfaces: ['table'],
      exports: { id: OVERRIDES_BY_RUN_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: overridesByRunExport },
      drill: {
        terminal: true,
        reason: 'The decision summary (GET /inseason/runs/{run_id}/decision-summary, bata-platform ADR 6036) publishes counts per queue and run; the lines behind them are decided, and read, on each app\'s Pending approvals page for the same run.',
      },
    },
    {
      path: 'matched-control',
      titleKey: 'strategic.matched.title',
      urlParams: [],
      component: () => import('./screens/MatchedControlScreen'),
      surfaces: ['tile'],
      exports: { id: MATCHED_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: matchedControlExport },
      drill: {
        terminal: true,
        reason: 'The estimate is stored per market and window by programme measurement (bata-platform ADR 9008); the matched door pairs behind it are not published.',
      },
    },
    {
      // The closed months, one per row (issue 226, giro 3 «Dopo» point 8): frozen or not yet, each
      // frozen one opening its pack. An old `?month=` link is moved to the pack's address.
      path: 'monthly-pack',
      titleKey: 'strategic.pack.title',
      urlParams: ['country', 'month'],
      component: () => import('./screens/MonthlyPackScreen'),
      surfaces: ['table'],
      exports: { id: PACK_MONTHS_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: monthlyPackMonthsExport },
      // A month row opens one market's frozen pack — the market's grain, for that month.
      drill: { to: '/strategic/monthly-pack/:month', grain: 'country' },
    },
    {
      // «Pacchetto» — one frozen pack, as a «Documento» (ADR 9053): read and printed whole; the
      // screen is the print. `?country=`; the month is the path. A drill, not in the rail.
      path: 'monthly-pack/:month',
      titleKey: 'strategic.pack.document.title',
      urlParams: ['country', 'month'],
      component: () => import('./screens/MonthlyPackDocumentScreen'),
      // No chart that draws data: the twelve weeks are «In arrivo». The tables are the content.
      surfaces: ['table'],
      exports: { id: PACK_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: monthlyPackExport },
      drill: {
        terminal: true,
        reason:
          'The pack is a frozen snapshot per market and closed month (programme_monthly_report, bata-platform ADR 9016). Its totals are sums the platform froze at the close; the rows behind them are the live queues, which have moved since, so a drill from the pack would show a different month than the one it reports.',
      },
    },
    // ── Salute (bata-reqs ADR 9003, docs/25 §5.4 B2, `docs/adr/3003`) ──────────────────────────────
    // Each report at the market and its groups. Every page reads the same scope — the market, the
    // grouping, the family, the source the planner chose and the trend's measure — through one parser
    // (`health/reports.ts`), so each declares all of them (ADR 9054). The newest run, week or price run
    // is the platform's answer when the URL names none, and the page names it.
    {
      path: 'health/availability',
      titleKey: 'strategic.health.availability.title',
      urlParams: HEALTH_URL_PARAMS,
      component: () => import('./health/HealthAvailabilityScreen'),
      surfaces: ['tile', 'chart', 'table'],
      tableViewReachable: true,
      exports: { id: HEALTH_EXPORT_ID.availability, formats: ['csv', 'xlsx'], resolve: healthExport('availability') },
      drill: { to: '/strategic/health/availability/by', grain: 'category' },
    },
    {
      path: 'health/availability/by',
      titleKey: 'strategic.health.availability.title',
      urlParams: HEALTH_URL_PARAMS,
      component: () => import('./health/HealthGroupsScreen'),
      surfaces: ['table'],
      exports: { id: HEALTH_BY_EXPORT_ID.availability, formats: ['csv', 'xlsx'], resolve: healthByExport('availability') },
      // A store or an article opens the network-health cockpit's page for the same run.
      drill: { to: '/inseason/network-health/store/:storeKey', grain: 'store', alsoTo: ['/inseason/network-health/article/:articleKey'] },
    },
    {
      path: 'health/coverage',
      titleKey: 'strategic.health.coverage.title',
      urlParams: HEALTH_URL_PARAMS,
      component: () => import('./health/HealthCoverageScreen'),
      surfaces: ['tile', 'chart', 'table'],
      tableViewReachable: true,
      exports: { id: HEALTH_EXPORT_ID.coverage, formats: ['csv', 'xlsx'], resolve: healthExport('coverage') },
      drill: { to: '/strategic/health/coverage/by', grain: 'category' },
    },
    {
      path: 'health/coverage/by',
      titleKey: 'strategic.health.coverage.title',
      urlParams: HEALTH_URL_PARAMS,
      component: () => import('./health/HealthGroupsScreen'),
      surfaces: ['table'],
      exports: { id: HEALTH_BY_EXPORT_ID.coverage, formats: ['csv', 'xlsx'], resolve: healthByExport('coverage') },
      drill: { to: '/inseason/network-health/store/:storeKey', grain: 'store', alsoTo: ['/inseason/network-health/article/:articleKey'] },
    },
    {
      path: 'health/sell-through',
      titleKey: 'strategic.health.sellThrough.title',
      urlParams: HEALTH_URL_PARAMS,
      component: () => import('./health/HealthSellThroughScreen'),
      surfaces: ['tile', 'chart', 'table'],
      tableViewReachable: true,
      exports: { id: HEALTH_EXPORT_ID['sell-through'], formats: ['csv', 'xlsx'], resolve: healthExport('sell-through') },
      drill: { to: '/strategic/health/sell-through/by', grain: 'category' },
    },
    {
      path: 'health/sell-through/by',
      titleKey: 'strategic.health.sellThrough.title',
      urlParams: HEALTH_URL_PARAMS,
      component: () => import('./health/HealthGroupsScreen'),
      surfaces: ['table'],
      exports: { id: HEALTH_BY_EXPORT_ID['sell-through'], formats: ['csv', 'xlsx'], resolve: healthByExport('sell-through') },
      drill: {
        terminal: true,
        reason: 'Sell-through is landed per country, fiscal week, store and article (inseason_sell_through_week, bata-platform ADR 9053): the article is its finest grain, and no queue decides on a sell-through.',
      },
    },
    {
      path: 'health/markdown-exposure',
      titleKey: 'strategic.health.markdown.title',
      urlParams: HEALTH_URL_PARAMS,
      component: () => import('./health/HealthMarkdownScreen'),
      surfaces: ['tile', 'chart', 'table'],
      tableViewReachable: true,
      exports: { id: HEALTH_EXPORT_ID['markdown-exposure'], formats: ['csv', 'xlsx'], resolve: healthExport('markdown-exposure') },
      drill: { to: '/strategic/health/markdown-exposure/by', grain: 'category' },
    },
    {
      path: 'health/markdown-exposure/by',
      titleKey: 'strategic.health.markdown.title',
      urlParams: HEALTH_URL_PARAMS,
      component: () => import('./health/HealthGroupsScreen'),
      surfaces: ['table'],
      exports: { id: HEALTH_BY_EXPORT_ID['markdown-exposure'], formats: ['csv', 'xlsx'], resolve: healthByExport('markdown-exposure') },
      // An article opens its price steps in the approvals queue (price steps carry no store).
      drill: { to: '/inseason/markdown/approvals', grain: 'article' },
    },
    {
      // «Ricerca» (`docs/adr/3004`): a pasted list of store × article pairs, each with its stock on the
      // as-of day and its sales over the window. The scope is in the URL; the list is not (it does not fit).
      path: 'health/lookup',
      titleKey: 'strategic.health.lookup.title',
      urlParams: ['asOf', 'bySize', 'country', 'salesFrom', 'salesTo'],
      component: () => import('./health/HealthLookupScreen'),
      surfaces: ['table'],
      exports: { id: LOOKUP_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: lookupExport },
      drill: {
        terminal: true,
        reason: 'A pair is one store × article, read on one stock day and one sales window (bata-platform POST /health/lookup); its sizes open under it on the page. It is a spot check against the client\'s own system, and no queue decides on it.',
      },
    },
  ],
});
